import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Download, ArrowRight } from "lucide-react"
import { useMemo } from 'react'

const researchCategories = ["All", "Market Analysis", "Industry Reports", "White Papers", "Technology"]

const researchItems = [
    {
        title: "Future of Financial Services 2024",
        category: "Industry Reports",
        date: "March 2024",
        summary: "Comprehensive analysis of emerging trends, technologies, and business models reshaping financial services.",
        downloadable: true,
        featured: true
    },
    {
        title: "ESG Investment Strategies",
        category: "White Papers",
        date: "February 2024",
        summary: "Deep dive into environmental, social, and governance factors driving investment decisions.",
        downloadable: true,
        featured: true
    },
    {
        title: "Digital Transformation in Banking",
        category: "Technology",
        date: "January 2024",
        summary: "Analysis of digital transformation strategies and their impact on traditional banking models.",
        downloadable: false,
        featured: false
    },
    {
        title: "Blockchain in Financial Markets",
        category: "Technology",
        date: "December 2023",
        summary: "Examination of blockchain technology's evolving role in financial markets and services.",
        downloadable: true,
        featured: false
    },
    {
        title: "Global Market Outlook 2024",
        category: "Market Analysis",
        date: "December 2023",
        summary: "Strategic market analysis and investment opportunities across global markets.",
        downloadable: true,
        featured: true
    }
]

export function ResearchPage() {
    const [selectedCategory, setSelectedCategory] = useState("All")
    
    const filteredResearchItems = useMemo(() => 
        researchItems.filter(item => 
            selectedCategory === "All" ? true : item.category === selectedCategory
        ), 
        [selectedCategory]
    )

    return (
        <div className="container mx-auto px-4 py-24">
            <div className="max-w-3xl mx-auto text-center mb-16">
                <h1 className="text-4xl font-bold tracking-tight mb-4">Research & Insights</h1>
                <p className="text-lg text-neutral-600 dark:text-neutral-400">
                    In-depth analysis and research publications from our global team of experts
                </p>
            </div>

            <div className="flex flex-wrap gap-2 justify-center mb-12">
                {researchCategories.map((category) => (
                    <Button
                        key={category}
                        variant={category === "All" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setSelectedCategory(category)}
                    >
                        {category}
                    </Button>
                ))}
            </div>

            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3 mb-12">
                {filteredResearchItems.map((item) => (
                    <Card key={item.title} className={`${item.featured ? 'border-2 border-primary' : ''}`}>
                        <CardHeader>
                            <div className="flex justify-between items-start mb-2">
                                <span className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                                    {item.category}
                                </span>
                                <span className="text-sm text-neutral-500">
                                    {item.date}
                                </span>
                            </div>
                            <CardTitle className="text-xl">{item.title}</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <p className="text-neutral-600 dark:text-neutral-400 mb-6">
                                {item.summary}
                            </p>
                            <div className="flex justify-between items-center">
                                <Button variant="outline">
                                    Read More
                                    <ArrowRight className="ml-2 h-4 w-4" />
                                </Button>
                                {item.downloadable && (
                                    <Button variant="ghost" size="icon">
                                        <Download className="h-4 w-4" />
                                    </Button>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            <div className="text-center">
                <Button variant="outline" size="lg">
                    Load More
                    <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
            </div>
        </div>
    )
} 